import React, { Children } from 'react';
import cx from 'classnames';
import MarkdownToJSX from 'markdown-to-jsx';
import { Heading, Level as HeadlingLevel, Size as HeadlingSize } from './heading';
import { Link } from './link';
import merge from 'lodash/merge';
import { isBrowser } from '@dx-ui/utilities-is-browser';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type Gap = 'sm' | 'md' | 'lg';

export type MarkdownProps = React.ComponentProps<typeof MarkdownToJSX> & {
  size?: Size;
  gap?: Gap;
  hasListItemMargin?: boolean;
};

type TagRendererProps = React.HTMLAttributes<HTMLElement> & {
  tag: 'ul' | 'ol' | 'li' | 'table' | 'thead' | 'th' | 'td' | 'b' | 'p' | 'div' | 'sup';
  children?: React.ReactNode;
};

const TagRenderer = ({ tag: Tag, children, ...props }: TagRendererProps) => {
  return <Tag {...props}>{children}</Tag>;
};

type BodyCopyRendererProps<
  T extends HTMLUListElement | HTMLOListElement | HTMLParagraphElement =
    | HTMLUListElement
    | HTMLOListElement
    | HTMLParagraphElement,
> = (T extends HTMLOListElement ? React.OlHTMLAttributes<T> : React.HTMLAttributes<T>) & {
  markdownSize?: Size;
};

const getBodyCopyClassName = (markdownSize?: Size, className?: string, baseClassName?: string) =>
  cx(
    'markdown--base',
    {
      'markdown--xs': markdownSize === 'xs',
      'markdown--sm': markdownSize === 'sm',
      'markdown--md': markdownSize === 'md',
      'markdown--lg': markdownSize === 'lg',
      'markdown--xl': markdownSize === 'xl',
    },
    baseClassName,
    className
  );

const UlRenderer = ({
  markdownSize,
  className,
  ...props
}: BodyCopyRendererProps<HTMLUListElement>) => (
  <ul {...props} className={getBodyCopyClassName(markdownSize, className, 'my-2 list-disc ps-8')} />
);

const OLRenderer = ({
  markdownSize,
  className,
  ...props
}: BodyCopyRendererProps<HTMLOListElement>) => (
  <ol
    {...props}
    className={getBodyCopyClassName(markdownSize, className, 'my-2 list-decimal ps-8')}
  />
);

const ParagraphRenderer = ({
  markdownSize,
  className,
  ...props
}: BodyCopyRendererProps<HTMLParagraphElement>) => (
  <p {...props} className={getBodyCopyClassName(markdownSize, className)} />
);

type HeadingRendererProps = React.ComponentProps<typeof Heading> & {
  level: Extract<HeadlingLevel, 1 | 2 | 3>;
  size?: Extract<HeadlingSize, '3xl' | '2xl' | 'xl' | 'lg'>;
  id?: string;
};

const HeadingRenderer = ({ level, size, id, children }: HeadingRendererProps) => {
  const sizeMap = {
    1: 'xl',
    2: 'lg',
    3: 'md',
    4: 'sm',
  } as const;
  return (
    <Heading
      id={id}
      level={level}
      size={size ?? sizeMap[level]}
      className={cx({
        'focus:ring lg:mb-3': level === 1,
        'lg:mb-2': level !== 1,
      })}
      tabIndex={level === 1 ? -1 : undefined}
    >
      {children}
    </Heading>
  );
};

const targetOpensInNewWindow = (href: string) => (href ? href.endsWith('target_blank') : false);

const LinkRenderer = ({ href, children, ...props }: React.HTMLProps<HTMLAnchorElement>) => {
  if (!href) {
    return null;
  }

  if (Children.count(children) === 0) {
    return null;
  }

  if (href.startsWith('#') && Array.isArray(children) && children.length === 0) {
    return <span id={href.replace('#', '')} />;
  }

  if (href === 'teconsent') {
    if (isBrowser && window?.location?.href?.includes('?trustarc')) {
      // `trustarc` is for analytics tracking (cookie preferences page) from GDPR countries
      return <a id={href} className="link--base underline" {...props} />;
    }
    return (
      <a id={href} className="link--base underline" {...props}>
        {children}
      </a>
    );
  }

  if (targetOpensInNewWindow(href)) {
    href = href.replace('/target_blank', '');
    props.target = '_blank';
    props.rel = 'noopener noreferrer';
  }

  if (href.endsWith('_backToTop')) {
    href = href.replace('_backToTop', '');
  }

  if (href.endsWith('Join_US')) {
    href = href.replace('Join_US', '');
  }

  return (
    <Link url={href} {...props}>
      {children}
    </Link>
  );
};

const isTable = (children: string) => children[0] === '|' && children.slice(-1) === '|';

/**
 * Normalize headings that are missing a space after the # characters.
 * e.g., "##INTRODUCTION" becomes "## INTRODUCTION"
 */
const normalizeHeadings = (text: string) => text.replace(/^(#{1,6})([^#\s])/gm, '$1 $2');

// Matches any HTML tag — consumed whole to protect attribute values (e.g. id="cke_bm_35C").
const HTML_TAG = /<[^>]*>/;

// Matches a full markdown link [text](url) — capturing link text and URL separately.
const MARKDOWN_LINK = /\[([^\]']*)\]\(([^)]*)\)/;

/**
 * Matches an underscore between two alphanumeric characters.
 * Uses lookahead/lookbehind so each underscore in a chain is matched independently,
 * e.g. all three underscores in dept_a_ext are caught in a single pass.
 */
const INTRA_WORD_UNDERSCORE = /(?<=[A-Za-z0-9])_(?=[A-Za-z0-9])/;

// Sources are module-level regex literals — safe to compose dynamically.
// eslint-disable-next-line security/detect-non-literal-regexp
const ESCAPE_UNDERSCORES_PATTERN = new RegExp(
  `${HTML_TAG.source}|${MARKDOWN_LINK.source}|${INTRA_WORD_UNDERSCORE.source}`,
  'g'
);

/**
 * Escapes intra-word underscores so markdown-to-jsx treats them as literal characters
 * rather than opening emphasis markers (e.g. email addresses like CMHCE_GM@Hilton.com).
 *
 * Processes three token types in priority order:
 * 1. HTML tags — returned untouched to protect attribute values (e.g. id="cke_bm_35C")
 * 2. Markdown links [text](url) — underscores escaped in link text only; URL is preserved
 *    to keep sentinel patterns like target_blank and Join_US intact
 * 3. Remaining intra-word underscores in plain text — escaped to \_
 */
const escapeIntraWordUnderscores = (text: string) =>
  text.replace(ESCAPE_UNDERSCORES_PATTERN, (match, linkText?: string, linkUrl?: string) => {
    if (match.startsWith('<')) {
      return match;
    }
    if (linkUrl !== undefined) {
      // eslint-disable-next-line security/detect-non-literal-regexp
      return `[${linkText!.replace(new RegExp(INTRA_WORD_UNDERSCORE.source, 'g'), '\\_')}](${linkUrl})`;
    }
    return '\\_';
  });

/**
 * Matches a dagger (†), double dagger (‡), or currency symbol (¤) so it can be wrapped in a superscript tag.
 */
const DAGGER_SYMBOL = /[†‡¤]/;

// Sources are module-level regex literals — safe to compose dynamically.
// eslint-disable-next-line security/detect-non-literal-regexp
const SUPERSCRIPT_CHARACTERS_PATTERN = new RegExp(
  `${HTML_TAG.source}|${DAGGER_SYMBOL.source}`,
  'g'
);

/**
 * Wraps dagger (†), double dagger (‡), and currency (¤) symbols in a superscript tag, skipping any
 * that appear inside HTML tags to avoid corrupting attribute values.
 */
const superscriptCharacters = (text: string) =>
  text.replace(SUPERSCRIPT_CHARACTERS_PATTERN, (match) =>
    match.startsWith('<') ? match : `<sup>${match}</sup>`
  );

/**
 * Check if a line is a list item (ordered or unordered)
 */
const isListItem = (line: string) => /^(\s*[-*+]|\s*\d+\.)\s/.test(line);

const mutateChildren = (children: string) => {
  const normalizedChildren = normalizeHeadings(children.trim());
  const escapedChildren = superscriptCharacters(escapeIntraWordUnderscores(normalizedChildren));

  if (isTable(escapedChildren)) {
    return escapedChildren;
  }

  const markdownArray = escapedChildren.split(/\r\n|\n/);

  return markdownArray
    .map((line, index) => {
      const isLastLine = index === markdownArray.length - 1;
      const nextLine = markdownArray[index + 1];

      if (line.trim() === '') {
        return isLastLine ? '' : '<span class="p-2 block" />\n\n';
      }

      // Preserve list continuity - don't add double newline between consecutive list items
      if (isListItem(line) && nextLine && isListItem(nextLine)) {
        return `${line}\n`;
      }

      return `${line}\n\n`;
    })
    .join('');
};

/**
 * A component for rendering markdown content using Prism's design system.
 * Provides consistent styling and proper heading hierarchy.
 */
export const Markdown = ({
  children,
  className,
  options,
  gap,
  size = 'md',
  id,
  hasListItemMargin = true,
  ...rest
}: MarkdownProps) => {
  const gapClasses = cx({
    'py-1': gap === 'sm',
    'py-2': gap === 'md',
    'py-4': gap === 'lg',
  });

  const mergedOptions: MarkdownProps['options'] = merge(
    {
      overrides: {
        a: {
          component: LinkRenderer,
        },
        h1: {
          component: HeadingRenderer,
          props: {
            level: 1,
          },
        },
        h2: {
          component: HeadingRenderer,
          props: {
            level: 2,
          },
        },
        h3: {
          component: HeadingRenderer,
          props: {
            level: 3,
          },
        },
        h4: {
          component: HeadingRenderer,
          props: {
            level: 4,
          },
        },
        p: {
          component: ParagraphRenderer,
          props: {
            markdownSize: size,
            className,
          },
        },
        table: {
          component: TagRenderer,
          props: {
            tag: 'table',
            className: 'table-auto -ml-2',
          },
        },
        thead: {
          component: TagRenderer,
          props: {
            tag: 'thead',
            className: 'border border-surface',
          },
        },
        th: {
          component: TagRenderer,
          props: {
            tag: 'th',
            className: 'py-3 px-2',
            style: {
              backgroundColor: 'color-mix(in srgb, var(--ds-color-background), black 13.7%)',
            },
          },
        },
        td: {
          component: TagRenderer,
          props: {
            tag: 'td',
            className: 'py-1 px-2',
          },
        },
        ul: {
          component: UlRenderer,
          props: {
            markdownSize: size,
          },
        },
        ol: {
          component: OLRenderer,
          props: {
            markdownSize: size,
          },
        },
        li: {
          component: TagRenderer,
          props: {
            tag: 'li',
            className: hasListItemMargin ? 'my-2' : undefined,
          },
        },
        sup: {
          component: TagRenderer,
          props: {
            tag: 'sup',
          },
        },
      },
    },
    options
  );

  return (
    <MarkdownToJSX
      {...rest}
      id={id}
      options={mergedOptions}
      className={cx(
        'markdown--base',
        gapClasses,
        {
          'markdown--md': size === 'md',
          'markdown--lg': size === 'lg',
        },
        className
      )}
    >
      {mutateChildren(children ?? '')}
    </MarkdownToJSX>
  );
};
